import { useCallback } from "react";
import { useMatchMedia } from "~/hooks/use-match-media";
import type { AspectRatiosType } from "~/styles/aspect-ratio";

type Size = {
  /** 横幅を指定する */
  w: number;
  /** 縦幅を指定する */
  h: number;
};

export type ImageSrcFormatArgs = {
  /** 画像のURL */
  imgSrc: string;
  /** サイズ */
  size?: Size;
  /** 画像フォーマットを指定する */
  f?: "webp" | "png" | "jpg";
  /** 圧縮率を指定する */
  q?: number;
  /** トリミングモード（wとhを両方指定した時のみ動作する） */
  t?: "center" | "attention" | "entropy" | "margin";
};

type DefaultImageSize = {
  [key in AspectRatiosType]: Size;
};

const pcImageSize: DefaultImageSize = {
  SQUARE: { w: 400, h: 400 },
  PORTRAIT: { w: 192, h: 273 },
  LANDSCAPE: { w: 304, h: 171 },
  INFINITY_CAROUSEL: { w: 1536, h: 672 },
};

const tabletImageSize: DefaultImageSize = {
  SQUARE: { w: 400, h: 400 },
  PORTRAIT: { w: 192, h: 273 },
  LANDSCAPE: { w: 240, h: 135 },
  INFINITY_CAROUSEL: { w: 1152, h: 504 },
};

const spImageSize: DefaultImageSize = {
  SQUARE: { w: 400, h: 400 },
  PORTRAIT: { w: 192, h: 273 },
  LANDSCAPE: { w: 240, h: 135 },
  INFINITY_CAROUSEL: { w: 1152, h: 504 },
};

type Args = { aspectRatio: AspectRatiosType };

export const useImageSrcFormat = (args: Args) => {
  const { aspectRatio } = args;
  const { isTabletSize, isPcSize } = useMatchMedia();
  const ImageSize = isPcSize ? pcImageSize : isTabletSize ? tabletImageSize : spImageSize;

  /**
   * 画像パスにGETパラメータを付与してリクエストを飛ばすことができる関数
   * @see https://confl.arms.dmm.com/pages/viewpage.action?pageId=100023318
   */
  const imageSrcFormat = useCallback(
    (args: ImageSrcFormatArgs) => {
      const { imgSrc, size, t = "margin", f = "webp", ...rest } = args;

      const { w, h } = size ?? ImageSize[aspectRatio];

      const formatWebpUrl = Object.entries({ w, h, t, f, ...rest })
        .map(([key, value]) => `${key}=${value}`)
        .join(`&`);

      const formatDefaultUrl = Object.entries({ w, h, t, ...rest })
        .map(([key, value]) => `${key}=${value}`)
        .join(`&`);

      return {
        src: `${imgSrc}?${formatWebpUrl}`,
        alternativeSrc: `${imgSrc}?${formatDefaultUrl}`,
      };
    },
    [aspectRatio, isTabletSize, isPcSize]
  );

  return { imageSrcFormat };
};
